"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
var exceptions_1 = require("@ikea-aoa/exceptions");
var DateFormatter_1 = __importDefault(require("../date/DateFormatter"));
var ProductPriceInformationSummaryTransformer_1 = __importDefault(require("./ProductPriceInformationSummaryTransformer"));
/**
 * Handles calculcation of total price for a list of products.
 */
var TotalPriceCalculator = /** @class */ (function () {
    function TotalPriceCalculator() {
    }
    /**
     * Calculates total price.
     *
     * @param shoppingProducts Shopping products.
     * @param dateFormat Date format. Example: YYYY-MM-DD.
     * @returns Instance of TotalPriceSummary.
     */
    TotalPriceCalculator.calculate = function (shoppingProducts, dateFormat) {
        if (!dateFormat) {
            throw new exceptions_1.Exception('Second parameter "dateFormat" was not given.');
        }
        var totalPrice = {
            inclTax: 0,
            exclTax: 0,
            familyInclTax: 0,
            familyExclTax: 0,
            regularInclTax: 0,
            regularExclTax: 0,
            onlineSaleable: false,
            newLowerPrice: false,
            family: false,
            temporary: false,
            validDateInterval: null,
            ecoPrice: 0,
            weeePrice: 0
        };
        var validDates = [];
        if (shoppingProducts.length > 0) {
            totalPrice.newLowerPrice = true;
        }
        for (var i = 0, max = shoppingProducts.length; i < max; i++) {
            var shoppingProduct = shoppingProducts[i];
            if (shoppingProduct.product.content) {
                var product = shoppingProduct.product.content;
                var productPriceInformationSummary = ProductPriceInformationSummaryTransformer_1.default.transform(product.priceInformation);
                var familyValidDate = this.calculateFamilyPrice(shoppingProduct, productPriceInformationSummary, totalPrice);
                var troValidDate = this.calculateTroPrice(productPriceInformationSummary, totalPrice);
                this.calculateNormalPrice(shoppingProduct, productPriceInformationSummary, totalPrice);
                this.calculateRegularPrice(shoppingProduct, productPriceInformationSummary, totalPrice);
                this.calculateFeePrices(shoppingProduct, totalPrice);
                totalPrice.onlineSaleable =
                    product.priceInformation.onlineSaleable === false ? false : totalPrice.onlineSaleable;
                totalPrice.newLowerPrice =
                    productPriceInformationSummary.newLowerPrice === false ? false : totalPrice.newLowerPrice;
                if (familyValidDate) {
                    validDates.push(familyValidDate);
                }
                if (troValidDate) {
                    validDates.push(troValidDate);
                }
            }
        }
        totalPrice.validDateInterval = this.getValidDateInterval(validDates, dateFormat);
        return totalPrice;
    };
    /**
     * Handles normal price.
     *
     * @param shoppingProduct Shopping product.
     * @param productPriceInformationSummary Sales price information.
     * @param totalPrice Total price model.
     */
    TotalPriceCalculator.calculateNormalPrice = function (shoppingProduct, productPriceInformationSummary, totalPrice) {
        // We should always have a normal price
        if (productPriceInformationSummary.price) {
            totalPrice.inclTax +=
                productPriceInformationSummary.price.priceInclTax * shoppingProduct.quantity;
            totalPrice.exclTax +=
                productPriceInformationSummary.price.priceExclTax * shoppingProduct.quantity;
        }
        else if (productPriceInformationSummary.regularPrice) {
            totalPrice.inclTax +=
                productPriceInformationSummary.regularPrice.priceInclTax * shoppingProduct.quantity;
            totalPrice.exclTax +=
                productPriceInformationSummary.regularPrice.priceExclTax * shoppingProduct.quantity;
        }
        else {
            throw new exceptions_1.Exception('Failed to calculate total price. Missing valid normal price in item: ' +
                Array.from(shoppingProduct.product.itemId).join(', ') +
                '.');
        }
    };
    /**
     * Handles family price.
     *
     * @param shoppingProduct Shopping product.
     * @param productPriceInformationSummary Sales price information.
     * @param totalPrice Total price model.
     * @returns Valid date.
     */
    TotalPriceCalculator.calculateFamilyPrice = function (shoppingProduct, productPriceInformationSummary, totalPrice) {
        if (productPriceInformationSummary.familyPrice) {
            totalPrice.family = true;
            totalPrice.familyInclTax +=
                productPriceInformationSummary.familyPrice.priceInclTax * shoppingProduct.quantity;
            totalPrice.familyExclTax +=
                productPriceInformationSummary.familyPrice.priceExclTax * shoppingProduct.quantity;
        }
        else {
            // If this was not a family product, family price increases with normal price
            totalPrice.familyInclTax +=
                productPriceInformationSummary.price.priceInclTax * shoppingProduct.quantity;
            totalPrice.familyExclTax +=
                productPriceInformationSummary.price.priceExclTax * shoppingProduct.quantity;
        }
        if (productPriceInformationSummary.temporaryFamilyPrice) {
            totalPrice.temporary = true;
            return {
                validFrom: DateFormatter_1.default.isoStringToDate(productPriceInformationSummary.familyPrice.validFromText),
                validTo: DateFormatter_1.default.isoStringToDate(productPriceInformationSummary.familyPrice.validToText)
            };
        }
        return null;
    };
    /**
     * Handles TRO price.
     *
     * @param productPriceInformationSummary Sales price information.
     * @param totalPrice Total price model.
     * @returns Valid date.
     */
    TotalPriceCalculator.calculateTroPrice = function (productPriceInformationSummary, totalPrice) {
        if (productPriceInformationSummary.timeRestrictedOffer) {
            totalPrice.temporary = true;
            return {
                validFrom: DateFormatter_1.default.isoStringToDate(productPriceInformationSummary.price.validFromText),
                validTo: DateFormatter_1.default.isoStringToDate(productPriceInformationSummary.price.validToText)
            };
        }
        return null;
    };
    /**
     * Handles Regular price.
     *
     * @param shoppingProduct Shopping product.
     * @param productPriceInformationSummary Sales price information.
     * @param totalPrice Total price model.
     */
    TotalPriceCalculator.calculateRegularPrice = function (shoppingProduct, productPriceInformationSummary, totalPrice) {
        // There was a check here before: if (priceInformation.regularPrice && !priceInformation.newLowerPrice)
        // The check does not make sense, so it has been removed
        if (productPriceInformationSummary.regularPrice) {
            totalPrice.regularInclTax +=
                productPriceInformationSummary.regularPrice.priceInclTax * shoppingProduct.quantity;
            totalPrice.regularExclTax +=
                productPriceInformationSummary.regularPrice.priceExclTax * shoppingProduct.quantity;
        }
        else {
            // If we don't have a special regular price, then the normal is regular
            totalPrice.regularInclTax +=
                productPriceInformationSummary.price.priceInclTax * shoppingProduct.quantity;
            totalPrice.regularExclTax +=
                productPriceInformationSummary.price.priceExclTax * shoppingProduct.quantity;
        }
    };
    /**
     * Handles fee price.
     *
     * @param shoppingProduct Shopping product.
     * @param totalPrice Total price model.
     */
    TotalPriceCalculator.calculateFeePrices = function (shoppingProduct, totalPrice) {
        if (shoppingProduct.product.content && shoppingProduct.product.content.priceInformation.fees) {
            for (var _i = 0, _a = shoppingProduct.product.content.priceInformation.fees; _i < _a.length; _i++) {
                var fee = _a[_i];
                if (fee.typeCode === 'ECO') {
                    totalPrice.ecoPrice += fee.amount * shoppingProduct.quantity;
                }
                else if (fee.typeCode === 'WEEE') {
                    totalPrice.weeePrice += fee.amount * shoppingProduct.quantity;
                }
            }
        }
    };
    /**
     * Returns the shortest date span of multiple dates.
     *
     * @param validDates Valid dates.
     * @param dateFormat Pattern to use.
     * @returns Date interval.
     */
    TotalPriceCalculator.getValidDateInterval = function (validDates, dateFormat) {
        if (validDates.length > 0) {
            var date = {
                validFrom: validDates[0].validFrom,
                validTo: validDates[0].validTo
            };
            for (var _i = 0, validDates_1 = validDates; _i < validDates_1.length; _i++) {
                var validDate = validDates_1[_i];
                if (validDate.validFrom > date.validFrom) {
                    date.validFrom = validDate.validFrom;
                }
                if (validDate.validTo < date.validTo) {
                    date.validTo = validDate.validTo;
                }
            }
            return {
                validFrom: DateFormatter_1.default.dateToString(date.validFrom, dateFormat),
                validTo: DateFormatter_1.default.dateToString(date.validTo, dateFormat)
            };
        }
        return null;
    };
    return TotalPriceCalculator;
}());
exports.default = TotalPriceCalculator;
//# sourceMappingURL=TotalPriceCalculator.js.map